function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Unwrap a block `flow` field into its name-keyed flow map, preserving the
 * caller's knowledge of WHICH shape it had: wrapped `{ flows: {...}, _diagram }`
 * or flat `{ <name>: def }`. All helpers below must write back through the
 * same shape — the e2e introspection helpers (mock-blocks.ts) and toLoadedApp
 * both read whichever shape was persisted.
 */
function unwrapBlockFlow(currentFlow: Record<string, unknown>): {
  wrapped: boolean
  flowMap: Record<string, unknown>
} {
  const wrapped = isRecord(currentFlow.flows)
  return {
    wrapped,
    flowMap: wrapped ? (currentFlow.flows as Record<string, unknown>) : currentFlow,
  }
}

/** Re-wrap a mutated flow map back into the block `flow` field, preserving
 *  the original wrapped/flat shape and passing `_diagram` (and any other
 *  sibling metadata) through untouched. */
function rewrapBlockFlow(
  currentFlow: Record<string, unknown>,
  wrapped: boolean,
  flowMap: Record<string, unknown>,
): Record<string, unknown> {
  if (wrapped) {
    return { ...currentFlow, flows: flowMap }
  }
  // Flat shape: the flow map IS the field. Non-flow metadata keys (e.g.
  // `_diagram`) live alongside the flow entries and are preserved because
  // callers build `flowMap` from the original entries.
  return flowMap
}

/**
 * Rename a flow declaration inside a block `flow` field: delete the old key,
 * insert the definition under the new key with `def.name` updated to match
 * (or to `defName` when given — the COR-2 undo leg restores the EXACT
 * original `def.name` instead of deriving it from the key). Pure — returns
 * a new payload (both shapes preserved, `_diagram` passes through). Returns
 * null when `oldKey` is not declared, so callers can fall back to their
 * never-persisted-draft path. `oldKey === newKey` is legal and just
 * normalizes `def.name` (a namespaced editor rename landing on the declared
 * bare key).
 *
 * CS-1: the map is rebuilt via Object.fromEntries + a computed-key literal
 * spread — both use CreateDataProperty, so a user-controlled `newKey` like
 * `"__proto__"` lands as an OWN key instead of invoking the inherited
 * Object.prototype setter (which left zero own keys → JSON-empty persist →
 * silent data loss). Reserved names are also rejected at ingress
 * (isReservedFlowName); this is defense in depth.
 */
export function renameFlowInBlockFlow(
  currentFlow: Record<string, unknown>,
  oldKey: string,
  newKey: string,
  defName?: string,
): { payload: Record<string, unknown>; flow: Record<string, unknown> } | null {
  const { wrapped, flowMap } = unwrapBlockFlow(currentFlow)
  if (!isRecord(flowMap[oldKey])) return null
  const renamed = { ...(flowMap[oldKey] as Record<string, unknown>), name: defName ?? newKey }
  const nextMap: Record<string, unknown> = Object.fromEntries(
    Object.entries(flowMap).filter(([key]) => key !== oldKey),
  )
  return {
    payload: rewrapBlockFlow(currentFlow, wrapped, { ...nextMap, [newKey]: renamed }),
    flow: renamed,
  }
}

/**
 * Remove a flow declaration from a block `flow` field. Pure — returns the
 * new payload plus the removed definition (the deleteFlow undo snapshot).
 * Returns null when `key` is not declared.
 */
export function removeFlowFromBlockFlow(
  currentFlow: Record<string, unknown>,
  key: string,
): { payload: Record<string, unknown>; removed: Record<string, unknown> } | null {
  const { wrapped, flowMap } = unwrapBlockFlow(currentFlow)
  if (!isRecord(flowMap[key])) return null
  const removed = flowMap[key] as Record<string, unknown>
  // Object.fromEntries (CreateDataProperty) keeps surviving keys — including
  // an own "__proto__" — as own keys (CS-1 defense in depth).
  const nextMap: Record<string, unknown> = Object.fromEntries(
    Object.entries(flowMap).filter(([mapKey]) => mapKey !== key),
  )
  return { payload: rewrapBlockFlow(currentFlow, wrapped, nextMap), removed }
}

/**
 * Insert (or overwrite) a flow declaration in a block `flow` field — the
 * restore counterpart of removeFlowFromBlockFlow, used by the bypass-only
 * `restoreFlow` undo tool to reinstate the exact snapshotted definition.
 * Shape preserved; a missing/empty field defaults to flat (matching
 * mergeFlowStepsForSave's creation case).
 */
export function upsertFlowIntoBlockFlow(
  currentFlow: Record<string, unknown>,
  key: string,
  flow: Record<string, unknown>,
): { payload: Record<string, unknown> } {
  const { wrapped, flowMap } = unwrapBlockFlow(currentFlow)
  return { payload: rewrapBlockFlow(currentFlow, wrapped, { ...flowMap, [key]: flow }) }
}

export function mergeFlowStepsForSave(input: {
  currentFlow: Record<string, unknown>
  flowName: string
  trigger?: unknown
  flowSteps: unknown[]
}): { payload: Record<string, unknown>; flow: Record<string, unknown> } {
  const wrappedFlows = isRecord(input.currentFlow.flows)
  const flowMap = wrappedFlows
    ? (input.currentFlow.flows as Record<string, unknown>)
    : input.currentFlow
  const existingFlow = isRecord(flowMap[input.flowName])
    ? (flowMap[input.flowName] as Record<string, unknown>)
    : {}
  const flow = {
    ...existingFlow,
    name: typeof existingFlow.name === 'string' ? existingFlow.name : input.flowName,
    trigger: typeof existingFlow.trigger === 'string'
      ? existingFlow.trigger
      : typeof input.trigger === 'string'
        ? input.trigger
        : 'event',
    steps: input.flowSteps,
  }

  if (wrappedFlows) {
    return {
      flow,
      payload: {
        ...input.currentFlow,
        flows: {
          ...flowMap,
          [input.flowName]: flow,
        },
      },
    }
  }

  return {
    flow,
    payload: {
      ...input.currentFlow,
      [input.flowName]: flow,
    },
  }
}
